'use client';

import React, { useState } from 'react';
import { ChevronRight, Edit3 } from 'lucide-react';

interface StepOccasionProps {
  initialValue?: string;
  onNext: (occasion: string) => void;
}

interface OccasionCard {
  id: string;
  label: string;
  emoji: string;
  subtitle: string;
  mood: string;
}

const OCCASIONS: OccasionCard[] = [
  { id: 'black-tie', label: 'Black Tie Gala', emoji: '🥂', subtitle: 'Formal evening event', mood: 'Opulent & dramatic' },
  { id: 'job-interview', label: 'Job Interview', emoji: '💼', subtitle: 'Professional setting', mood: 'Polished & confident' },
  { id: 'casual-brunch', label: 'Casual Brunch', emoji: '🥐', subtitle: 'Weekend daytime', mood: 'Relaxed & effortless' },
  { id: 'date-night', label: 'Date Night', emoji: '🕯️', subtitle: 'Romantic dinner', mood: 'Alluring & refined' },
  { id: 'festival', label: 'Music Festival', emoji: '🎪', subtitle: 'Outdoor concert', mood: 'Bold & free-spirited' },
  { id: 'garden-party', label: 'Garden Party', emoji: '🌸', subtitle: 'Outdoor social', mood: 'Whimsical & fresh' },
  { id: 'business-casual', label: 'Business Casual', emoji: '📋', subtitle: 'Office or meeting', mood: 'Smart & approachable' },
  { id: 'beach-vacation', label: 'Beach Vacation', emoji: '🌊', subtitle: 'Tropical getaway', mood: 'Breezy & vibrant' },
  { id: 'cocktail-party', label: 'Cocktail Party', emoji: '🍸', subtitle: 'Semi-formal evening', mood: 'Chic & elevated' },
  { id: 'weekend-getaway', label: 'Weekend Getaway', emoji: '🚂', subtitle: 'Travel & explore', mood: 'Comfortable & stylish' },
  { id: 'art-gallery', label: 'Art Gallery Opening', emoji: '🖼️', subtitle: 'Cultural evening', mood: 'Artistic & distinctive' },
  { id: 'wedding-guest', label: 'Wedding Guest', emoji: '💐', subtitle: 'Celebration ceremony', mood: 'Graceful & joyful' },
];

export default function StepOccasion({ initialValue, onNext }: StepOccasionProps) {
  const [selected, setSelected] = useState<string>(initialValue ?? '');
  const [customMode, setCustomMode] = useState(false);
  const [customText, setCustomText] = useState('');

  const handleSelect = (label: string) => {
    setSelected(label);
    setCustomMode(false);
  };

  const handleCustomToggle = () => {
    setCustomMode(true);
    setSelected('');
  };

  const handleNext = () => {
    const value = customMode ? customText.trim() : selected;
    if (!value) return;
    onNext(value);
  };

  const canProceed = customMode ? customText.trim().length > 2 : selected.length > 0;

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 2xl:px-16 py-6 sm:py-10 lg:py-16">
      {/* Heading */}
      <div className="mb-6 sm:mb-10 animate-fade-in-up">
        <p className="text-xs font-semibold uppercase tracking-widest mb-2 sm:mb-3" style={{ color: '#C9A96E', letterSpacing: '0.12em' }}>
          Step 1 of 4
        </p>
        <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl xl:text-6xl mb-2 sm:mb-3" style={{ color: '#1A1A1A' }}>
          What&apos;s the occasion?
        </h1>
        <p className="text-sm sm:text-base lg:text-lg" style={{ color: 'hsl(30 8% 52%)' }}>
          Every great outfit starts with knowing where you&apos;re going.
        </p>
      </div>

      {/* Occasion Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-6 gap-2 sm:gap-3 lg:gap-4 mb-5 sm:mb-6">
        {OCCASIONS.map((occ) => {
          const isSelected = selected === occ.label;
          return (
            <button
              key={`occasion-${occ.id}`}
              onClick={() => handleSelect(occ.label)}
              className={`
                relative text-left p-3 sm:p-4 lg:p-5 rounded-2xl border-2 transition-all duration-200 group
                ${isSelected
                  ? 'border-[#C9A96E] bg-[#C9A96E]/8 scale-[1.02]'
                  : 'border-transparent hover:border-[#C9A96E]/40 hover:bg-white/60 hover:scale-[1.03] hover:-translate-y-0.5'
                }
              `}
              style={{
                backgroundColor: isSelected ? 'rgba(201,169,110,0.08)' : 'white',
                boxShadow: isSelected
                  ? '0 0 0 0 transparent, 0 8px 24px rgba(201,169,110,0.2)'
                  : '0 1px 4px rgba(0,0,0,0.06)',
              }}
              onMouseEnter={(e) => {
                if (!isSelected) (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                if (!isSelected) (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)';
              }}
              aria-pressed={isSelected}
            >
              {isSelected && (
                <div
                  className="absolute top-2 right-2 sm:top-3 sm:right-3 w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: '#C9A96E' }}
                >
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
              <span className="text-xl sm:text-2xl lg:text-3xl block mb-1.5 sm:mb-2">{occ.emoji}</span>
              <span className="block text-xs sm:text-sm font-semibold leading-tight mb-0.5 sm:mb-1" style={{ color: '#1A1A1A' }}>
                {occ.label}
              </span>
              <span className="block text-xs" style={{ color: 'hsl(30 8% 52%)' }}>
                {occ.subtitle}
              </span>
              <span
                className="hidden sm:block text-xs mt-2 italic"
                style={{ color: '#C9A96E' }}
              >
                {occ.mood}
              </span>
            </button>
          );
        })}

        {/* Custom occasion card */}
        <button
          onClick={handleCustomToggle}
          className={`
            text-left p-3 sm:p-4 lg:p-5 rounded-2xl border-2 transition-all duration-200
            ${customMode
              ? 'border-[#C9A96E] scale-[1.02]'
              : 'border-dashed hover:border-[#C9A96E]/60 hover:scale-[1.03] hover:-translate-y-0.5'
            }
          `}
          style={{
            borderColor: customMode ? '#C9A96E' : 'hsl(35 20% 80%)',
            backgroundColor: customMode ? 'rgba(201,169,110,0.06)' : 'transparent',
            boxShadow: customMode ? '0 8px 24px rgba(201,169,110,0.15)' : undefined,
          }}
          onMouseEnter={(e) => {
            if (!customMode) (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
          }}
          onMouseLeave={(e) => {
            if (!customMode) (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
          }}
        >
          <Edit3 size={20} className="mb-1.5 sm:mb-2" style={{ color: '#C9A96E' }} />
          <span className="block text-xs sm:text-sm font-semibold mb-0.5 sm:mb-1" style={{ color: '#1A1A1A' }}>
            Something else
          </span>
          <span className="block text-xs" style={{ color: 'hsl(30 8% 52%)' }}>
            Describe your own occasion
          </span>
        </button>
      </div>

      {/* Custom text input */}
      {customMode && (
        <div className="mb-6 sm:mb-8 animate-fade-in-up max-w-xl">
          <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
            Describe your occasion
          </label>
          <input
            type="text"
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            placeholder="e.g. My best friend's rooftop birthday party..."
            className="w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all duration-150 focus:ring-2"
            style={{
              borderColor: 'hsl(35 20% 88%)',
              backgroundColor: 'white',
              color: '#1A1A1A',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#C9A96E';
              e.target.style.boxShadow = '0 0 0 3px rgba(201,169,110,0.15)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'hsl(35 20% 88%)';
              e.target.style.boxShadow = 'none';
            }}
            maxLength={120}
            autoFocus
          />
          <p className="text-xs mt-1.5 tabular-nums" style={{ color: 'hsl(30 8% 52%)' }}>
            {customText.length}/120
          </p>
        </div>
      )}

      {/* CTA */}
      <div className="flex items-center gap-3 sm:gap-4">
        <button
          onClick={handleNext}
          disabled={!canProceed}
          className={`
            inline-flex items-center gap-2 sm:gap-2.5 px-6 sm:px-8 py-3 sm:py-3.5 rounded-full text-sm font-semibold
            transition-all duration-150 active:scale-95
            ${canProceed
              ? 'hover:opacity-90 hover:shadow-lg'
              : 'opacity-40 cursor-not-allowed'
            }
          `}
          style={{
            backgroundColor: canProceed ? '#1A1A1A' : '#8A8078',
            color: 'white',
          }}
        >
          Continue to Budget
          <ChevronRight size={16} />
        </button>
        {selected && !customMode && (
          <span className="text-xs sm:text-sm hidden sm:inline" style={{ color: 'hsl(30 8% 52%)' }}>
            <span className="font-medium" style={{ color: '#1A1A1A' }}>{selected}</span> selected
          </span>
        )}
      </div>
    </div>
  );
}